import React, { useState, useEffect, useCallback } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import './AzureMonitoringScreen.css';

interface FunctionLog {
    id: number;
    function_name: string;
    action_type: string;
    date_cotation: string | null;
    status: string;
    societes_count: number;
    cotations_count: number;
    indices_count: number;
    dividendes_count: number;
    corrections_count: number;
    pdf_file: string | null;
    message: string | null;
    duration_seconds: number | null;
    started_at: string;
    completed_at: string | null;
}

interface LogStats {
    total_logs: number;
    last_7_days: {
        status: { [key: string]: number };
        action_type: { [key: string]: number };
        total_cotations: number;
        total_indices: number;
        total_dividendes: number;
        total_corrections: number;
    };
    latest_import: {
        date_cotation: string | null;
        started_at: string | null;
        cotations_count: number;
    } | null;
}

type TabType = 'overview' | 'imports' | 'caches' | 'alerts' | 'marketing' | 'all';

const ACTION_TYPES = {
    IMPORT: { label: 'Import', icon: 'cloud_download', color: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
    CORRECTION: { label: 'Correction', icon: 'build', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
    CACHE: { label: 'Cache', icon: 'memory', color: '#10b981', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
    ALERT_CHECK: { label: 'Alertes Prix', icon: 'notifications', color: '#ec4899', gradient: 'linear-gradient(135deg, #ec4899, #db2777)' },
    TECH_ALERT: { label: 'Signaux Pro', icon: 'psychology', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
    PRO_ALERT: { label: 'Signaux Pro', icon: 'psychology', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
    REPORTING: { label: 'Rapport', icon: 'description', color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)' },
    AI_NARRATIVES: { label: 'Narratifs IA', icon: 'auto_awesome', color: '#6366f1', gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)' },
    LEADERBOARD: { label: 'Leaderboard', icon: 'leaderboard', color: '#14b8a6', gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)' },
    MARKETING: { label: 'Marketing', icon: 'campaign', color: '#f97316', gradient: 'linear-gradient(135deg, #f97316, #ea580c)' },
};

const AzureMonitoringScreen: React.FC = () => {
    const { token } = useAuth();
    const [logs, setLogs] = useState<FunctionLog[]>([]);
    const [stats, setStats] = useState<LogStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>('overview');
    const [filter, setFilter] = useState({ action_type: '', status: '' });

    const fetchLogs = useCallback(async () => {
        try {
            if (!token) {
                setError('Non authentifié. Veuillez vous connecter.');
                return;
            }

            const params = new URLSearchParams();
            if (filter.action_type) params.append('action_type', filter.action_type);
            if (filter.status) params.append('status', filter.status);
            params.append('limit', '100');

            const response = await fetch(`${API_URL}/v1/admin/function-logs?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 401) {
                setError('Session expirée. Veuillez vous reconnecter.');
                return;
            }
            if (response.status === 403) {
                setError('Accès interdit. Vous devez être administrateur.');
                return;
            }
            if (!response.ok) {
                const text = await response.text();
                throw new Error(`Erreur ${response.status}: ${text}`);
            }

            const data = await response.json();
            setLogs(data);
            setError(null);
        } catch (err: any) {
            setError(err.message);
        }
    }, [filter]);

    const fetchStats = useCallback(async () => {
        try {
            if (!token) return;

            const response = await fetch(`${API_URL}/v1/admin/function-logs/stats`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setStats(data);
            }
        } catch (err: any) {
            console.error('Stats error:', err);
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchLogs(), fetchStats()]);
            setLoading(false);
        };
        loadData();
        // Auto-refresh every 60 seconds
        const interval = setInterval(() => {
            fetchLogs();
            fetchStats();
        }, 60000);
        return () => clearInterval(interval);
    }, [fetchLogs, fetchStats]);

    const getStatusBadge = (status: string) => {
        const styles: { [key: string]: { bg: string; icon: string } } = {
            'SUCCESS': { bg: '#10b981', icon: 'check_circle' },
            'FAILED': { bg: '#ef4444', icon: 'error' },
            'PARTIAL': { bg: '#f59e0b', icon: 'warning' },
            'RUNNING': { bg: '#3b82f6', icon: 'sync' },
            'SKIPPED': { bg: '#6b7280', icon: 'skip_next' },
        };
        const style = styles[status] || { bg: '#64748b', icon: 'help' };
        return (
            <span className="status-pill" style={{ backgroundColor: style.bg }}>
                <i className={`material-icons ${status === 'RUNNING' ? 'spin' : ''}`}>{style.icon}</i>
                {status}
            </span>
        );
    };

    const getActionBadge = (action: string) => {
        const config = ACTION_TYPES[action as keyof typeof ACTION_TYPES] || { label: action, icon: 'bolt', color: '#64748b' };
        return (
            <span className="action-badge" style={{ borderColor: config.color, color: config.color }}>
                <i className="material-icons" style={{ color: config.color }}>{config.icon}</i>
                {config.label}
            </span>
        );
    };

    const formatDuration = (seconds: number | null) => {
        if (seconds === null || seconds === undefined) return '-';
        if (seconds < 60) return `${seconds.toFixed(1)}s`;
        const mins = Math.floor(seconds / 60);
        const secs = Math.round(seconds % 60);
        return `${mins}m ${secs}s`;
    };

    const formatDateTime = (dateStr: string | null) => {
        if (!dateStr) return '-';
        try {
            const date = new Date(dateStr);
            return date.toLocaleString('fr-FR', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        } catch {
            return dateStr;
        }
    };

    const formatRelativeTime = (dateStr: string | null) => {
        if (!dateStr) return '-';
        try {
            const date = new Date(dateStr);
            const now = new Date();
            const diffMs = now.getTime() - date.getTime();
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMins / 60);
            const diffDays = Math.floor(diffHours / 24);

            if (diffMins < 1) return "À l'instant";
            if (diffMins < 60) return `Il y a ${diffMins} min`;
            if (diffHours < 24) return `Il y a ${diffHours}h`;
            if (diffDays === 1) return 'Hier';
            return `Il y a ${diffDays}j`;
        } catch {
            return dateStr;
        }
    };

    const handleRefresh = () => {
        fetchLogs();
        fetchStats();
    };

    // Get filtered logs based on active tab
    const getFilteredLogs = () => {
        switch (activeTab) {
            case 'imports':
                return logs.filter(l => l.action_type === 'IMPORT' || l.action_type === 'CORRECTION');
            case 'caches':
                return logs.filter(l => l.action_type === 'CACHE');
            case 'alerts':
                return logs.filter(l => ['ALERT_CHECK', 'TECH_ALERT', 'PRO_ALERT'].includes(l.action_type));
            case 'marketing':
                return logs.filter(l => ['REPORTING', 'AI_NARRATIVES', 'LEADERBOARD', 'MARKETING'].includes(l.action_type));
            case 'all':
                return logs;
            default:
                return logs.slice(0, 10);
        }
    };

    // Get success rate for action type
    const getSuccessRate = (actionType: string) => {
        const actionLogs = logs.filter(l => l.action_type === actionType);
        if (actionLogs.length === 0) return 0;
        const successCount = actionLogs.filter(l => l.status === 'SUCCESS').length;
        return Math.round((successCount / actionLogs.length) * 100);
    };

    // Get last log for action type
    const getLastLog = (actionType: string) => {
        return logs.find(l => l.action_type === actionType);
    };

    // Render stat card for overview
    const StatCard: React.FC<{ actionType: keyof typeof ACTION_TYPES }> = ({ actionType }) => {
        const config = ACTION_TYPES[actionType];
        const lastLog = getLastLog(actionType);
        const successRate = getSuccessRate(actionType);
        const count = stats?.last_7_days.action_type[actionType] || 0;

        return (
            <div className="stat-card">
                <div className="stat-card-header" style={{ background: config.gradient }}>
                    <i className="material-icons">{config.icon}</i>
                    <span>{config.label}</span>
                </div>
                <div className="stat-card-body">
                    <div className="stat-row">
                        <span className="stat-label">Exécutions (7j)</span>
                        <span className="stat-value">{count}</span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">Taux de succès</span>
                        <span className={`stat-value ${successRate >= 80 ? 'success' : successRate >= 50 ? 'warning' : 'danger'}`}>
                            {successRate}%
                        </span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">Dernière exécution</span>
                        <span className="stat-value small">{formatRelativeTime(lastLog?.started_at || null)}</span>
                    </div>
                    {lastLog && (
                        <div className="stat-row last-status">
                            <span className="stat-label">Statut</span>
                            {getStatusBadge(lastLog.status)}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Render report section
    const ReportSection: React.FC = () => {
        const totalSuccess = stats?.last_7_days.status['SUCCESS'] || 0;
        const totalFailed = stats?.last_7_days.status['FAILED'] || 0;
        const totalPartial = stats?.last_7_days.status['PARTIAL'] || 0;
        const totalLogs = totalSuccess + totalFailed + totalPartial;

        return (
            <div className="report-section">
                <h3><i className="material-icons">analytics</i> Rapport des 7 derniers jours</h3>
                <div className="report-grid">
                    <div className="report-item">
                        <div className="report-circle success">
                            <span className="report-number">{totalSuccess}</span>
                        </div>
                        <span className="report-label">Succès</span>
                    </div>
                    <div className="report-item">
                        <div className="report-circle danger">
                            <span className="report-number">{totalFailed}</span>
                        </div>
                        <span className="report-label">Échecs</span>
                    </div>
                    <div className="report-item">
                        <div className="report-circle warning">
                            <span className="report-number">{totalPartial}</span>
                        </div>
                        <span className="report-label">Partiels</span>
                    </div>
                    <div className="report-item">
                        <div className="report-circle primary">
                            <span className="report-number">{totalLogs}</span>
                        </div>
                        <span className="report-label">Total</span>
                    </div>
                </div>

                <div className="report-details">
                    <div className="report-detail-item">
                        <i className="material-icons" style={{ color: '#3b82f6' }}>show_chart</i>
                        <span>{stats?.last_7_days.total_cotations || 0}</span>
                        <label>Cotations importées</label>
                    </div>
                    <div className="report-detail-item">
                        <i className="material-icons" style={{ color: '#f59e0b' }}>trending_up</i>
                        <span>{stats?.last_7_days.total_indices || 0}</span>
                        <label>Indices mis à jour</label>
                    </div>
                    <div className="report-detail-item">
                        <i className="material-icons" style={{ color: '#10b981' }}>payments</i>
                        <span>{stats?.last_7_days.total_dividendes || 0}</span>
                        <label>Dividendes</label>
                    </div>
                    <div className="report-detail-item">
                        <i className="material-icons" style={{ color: '#8b5cf6' }}>auto_fix_high</i>
                        <span>{stats?.last_7_days.total_corrections || 0}</span>
                        <label>Corrections</label>
                    </div>
                </div>
            </div>
        );
    };

    const filteredLogs = getFilteredLogs();

    return (
        <SiteLayout>
            <Header icon='monitor_heart' title='Monitoring Azure Functions' />
            <div className="monitoring-page">
                {/* Tab Navigation */}
                <div className="tab-navigation">
                    <button
                        className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        <i className="material-icons">dashboard</i>
                        Vue d'ensemble
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'imports' ? 'active' : ''}`}
                        onClick={() => setActiveTab('imports')}
                    >
                        <i className="material-icons">cloud_download</i>
                        Imports & Corrections
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'caches' ? 'active' : ''}`}
                        onClick={() => setActiveTab('caches')}
                    >
                        <i className="material-icons">memory</i>
                        Caches
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'alerts' ? 'active' : ''}`}
                        onClick={() => setActiveTab('alerts')}
                    >
                        <i className="material-icons">notifications</i>
                        Alertes
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'marketing' ? 'active' : ''}`}
                        onClick={() => setActiveTab('marketing')}
                    >
                        <i className="material-icons">campaign</i>
                        Marketing & IA
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveTab('all')}
                    >
                        <i className="material-icons">list</i>
                        Tous les logs
                    </button>
                    <button className="btn-refresh" onClick={handleRefresh}>
                        <i className="material-icons">refresh</i>
                    </button>
                </div>

                {/* Main Content */}
                <div className="monitoring-content">
                    {loading ? (
                        <div className="state-container">
                            <div className="loading-spinner"></div>
                            <p>Chargement des données...</p>
                        </div>
                    ) : error ? (
                        <div className="state-container error">
                            <i className="material-icons">error_outline</i>
                            <h3>Erreur de chargement</h3>
                            <p>{error}</p>
                            <button onClick={handleRefresh} className="btn-retry">
                                <i className="material-icons">refresh</i>
                                Réessayer
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Overview Tab */}
                            {activeTab === 'overview' && (
                                <div className="overview-content">
                                    <ReportSection />

                                    <h3 className="section-title">
                                        <i className="material-icons">apps</i>
                                        État des fonctions
                                    </h3>
                                    <div className="stat-cards-grid">
                                        <StatCard actionType="IMPORT" />
                                        <StatCard actionType="CACHE" />
                                        <StatCard actionType="CORRECTION" />
                                        <StatCard actionType="ALERT_CHECK" />
                                    </div>

                                    {stats?.latest_import && (
                                        <div className="latest-import-banner">
                                            <i className="material-icons">cloud_done</i>
                                            <div className="banner-content">
                                                <strong>Dernier import réussi</strong>
                                                <span>Date de cotation: {stats.latest_import.date_cotation || 'N/A'}</span>
                                                <span>{stats.latest_import.cotations_count} cotations • {formatRelativeTime(stats.latest_import.started_at)}</span>
                                            </div>
                                        </div>
                                    )}

                                    <h3 className="section-title">
                                        <i className="material-icons">history</i>
                                        Activité récente
                                    </h3>
                                </div>
                            )}

                            {/* Cache Tab Header */}
                            {activeTab === 'caches' && (
                                <div className="tab-header">
                                    <h3><i className="material-icons">memory</i> Mises à jour des Caches</h3>
                                    <p>Suivi des recalculs de valorisations et données de marché</p>
                                </div>
                            )}

                            {/* Imports Tab Header */}
                            {activeTab === 'imports' && (
                                <div className="tab-header">
                                    <h3><i className="material-icons">cloud_download</i> Imports & Corrections</h3>
                                    <p>Historique des imports de rapports BRVM et corrections de données</p>
                                </div>
                            )}

                            {/* Alerts Tab Header */}
                            {activeTab === 'alerts' && (
                                <div className="tab-header">
                                    <h3><i className="material-icons">notifications</i> Vérification des Alertes</h3>
                                    <p>Logs des vérifications automatiques d'alertes de prix</p>
                                </div>
                            )}

                            {/* Marketing Tab Header */}
                            {activeTab === 'marketing' && (
                                <div className="tab-header">
                                    <h3><i className="material-icons">campaign</i> Marketing & IA</h3>
                                    <p>Rapports PDF, narratifs IA, leaderboards et emails marketing</p>
                                </div>
                            )}

                            {/* All Tab Header */}
                            {activeTab === 'all' && (
                                <div className="tab-header">
                                    <h3><i className="material-icons">list</i> Tous les logs</h3>
                                    <div className="filter-row">
                                        <select
                                            value={filter.action_type}
                                            onChange={e => setFilter({ ...filter, action_type: e.target.value })}
                                        >
                                            <option value="">Toutes les actions</option>
                                            {Object.entries(ACTION_TYPES).map(([key, val]) => (
                                                <option key={key} value={key}>{val.label}</option>
                                            ))}
                                        </select>
                                        <select
                                            value={filter.status}
                                            onChange={e => setFilter({ ...filter, status: e.target.value })}
                                        >
                                            <option value="">Tous les statuts</option>
                                            <option value="SUCCESS">Succès</option>
                                            <option value="FAILED">Échec</option>
                                            <option value="PARTIAL">Partiel</option>
                                            <option value="SKIPPED">Ignoré</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Logs Table */}
                            {filteredLogs.length === 0 ? (
                                <div className="state-container empty">
                                    <i className="material-icons">inbox</i>
                                    <h3>Aucun log disponible</h3>
                                    <p>Les logs apparaîtront ici après l'exécution des fonctions.</p>
                                </div>
                            ) : (
                                <div className="logs-list">
                                    {filteredLogs.map((log) => (
                                        <div key={log.id} className={`log-card ${log.status.toLowerCase()}`}>
                                            <div className="log-card-header">
                                                {getActionBadge(log.action_type)}
                                                {getStatusBadge(log.status)}
                                                <span className="log-function-name">{log.function_name}</span>
                                                <span className="log-time">{formatDateTime(log.started_at)}</span>
                                            </div>
                                            <div className="log-card-body">
                                                {log.date_cotation && (
                                                    <div className="log-info">
                                                        <i className="material-icons">event</i>
                                                        <span>Date cotation: <strong>{log.date_cotation}</strong></span>
                                                    </div>
                                                )}
                                                <div className="log-metrics">
                                                    {log.cotations_count > 0 && (
                                                        <span className="metric"><i className="material-icons">show_chart</i> {log.cotations_count} cotations</span>
                                                    )}
                                                    {log.indices_count > 0 && (
                                                        <span className="metric"><i className="material-icons">trending_up</i> {log.indices_count} indices</span>
                                                    )}
                                                    {log.dividendes_count > 0 && (
                                                        <span className="metric"><i className="material-icons">payments</i> {log.dividendes_count} dividendes</span>
                                                    )}
                                                    {log.corrections_count > 0 && (
                                                        <span className="metric"><i className="material-icons">auto_fix_high</i> {log.corrections_count} corrections</span>
                                                    )}
                                                    {log.duration_seconds && (
                                                        <span className="metric duration"><i className="material-icons">timer</i> {formatDuration(log.duration_seconds)}</span>
                                                    )}
                                                </div>
                                                {log.message && (
                                                    <div className="log-message">
                                                        <i className="material-icons">info</i>
                                                        <span>{log.message}</span>
                                                    </div>
                                                )}
                                                {log.pdf_file && (
                                                    <div className="log-file">
                                                        <i className="material-icons">picture_as_pdf</i>
                                                        <span>{log.pdf_file}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </SiteLayout>
    );
};

export default AzureMonitoringScreen;
